//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { PojoMap } from 'pojo-maps';

import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import {
  addLike2BuyV2Items,
  archiveLike2BuyV2Items,
  clearLike2BuyV2ItemCache,
  getLike2BuyV2Items,
  getPinnedLike2BuyV2Items,
  materializeLike2BuyV2ItemsByCuid,
  setPinnedLike2BuyV2Items,
} from '@apis/like2buy-v2/like2BuyV2ModerationApi';
import {
  ILike2BuyV2MediaItem,
  Like2BuyV2ModerationItemFilter,
  NormalizedLike2BuyV2Items,
  Like2BuyV2ModerationStatus,
} from '@apis/like2buy-v2/like2BuyV2ModerationModel';
import {
  getLike2BuyV2PreviewUrl,
  findLike2BuyV2Stores,
} from '@apis/like2buy-v2/like2BuyV2StoreApi';
import { ILike2BuyV2Store } from '@apis/like2buy-v2/like2BuyV2StoreModel';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { IUiaModalClosedResponse } from '@legacy-components/common/universal-image-actions/models/events';
import {
  LIKE2BUY_V2_ITEM_REQUEST_LIMIT,
  MAXIMUM_PINNED_ITEMS_COUNT,
} from '@like2buy-v2/Like2BuyV2UIConstants';
import {
  addLike2BuyV2ItemsFailedToast,
  addLike2BuyV2ItemsSucceededToast,
  archiveLike2BuyV2ItemsFailedToast,
  like2BuyV2MaxPinnedItemsWarningToast,
  pinLike2BuyV2ItemsFailedToast,
  reorderLike2BuyV2PinnedItemsFailedToast,
  unarchiveLike2BuyV2ItemsFailedToast,
  unpinLike2BuyV2ItemsFailedToast,
  maxPinsLike2BuyV2ItemsFailedToast,
  pinLike2BuyV2ItemsSucceededToast,
  unpinLike2BuyV2ItemsSucceededToast,
  archiveLike2BuyV2ItemsSucceededToast,
  unarchiveLike2BuyV2ItemsSucceededToast,
} from '@like2buy-v2/like2BuyV2ModerationToasts';
import {
  toLike2BuyV2MediaItems,
  getLike2BuyV2StatusForRequest,
  toLike2BuyV2NormalizedMediaItems,
  getDoesItemNeedRightsAndProducts,
  getDoesItemNeedProducts,
  getDoesItemNeedRights,
} from '@like2buy-v2/like2BuyV2UIUtils';
import Cuid from '@models/Cuid';
import Like2BuyV2StoreId from '@models/Like2BuyV2StoreId';
import PartialCuid from '@models/PartialCuid';
import { SocialNetwork } from '@models/SocialNetwork';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { getClientId } from '@redux/selectors/getClientId';
import { DEFAULT_TIKTOK_LIKE2BUY_FILTER } from '@tiktok/components/like2buy/TikTokLike2BuyUIConstants';
import {
  getTikTokLike2BuyOrderedItems,
  getTikTokLike2BuyStoreId,
  getTikTokLike2BuyPinnedItems as getTikTokLike2BuyPinnedItemsFromStore,
  getTikTokLike2BuyCurrentItemFilter,
  getTikTokLike2Buy,
  getTikTokLike2BuyFlags,
  getTikTokLike2BuySelectedItems,
  getTikTokLike2BuyFilteredOrderedItems,
} from '@tiktok/redux/like2buy/selectors/getTikTokLike2Buy';
import {
  ITikTokLike2BuyItemsSearchResult,
  ITikTokLike2BuyState,
} from '@tiktok/redux/like2buy/tiktokLike2BuyModel';
import { getTikTokUserId } from '@tiktok/redux/user/selectors/getTikTokUser';
import { isDefined, isUndefined } from '@utils/filterPredicates';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';
import normalize, { Normalized } from '@utils/normalize';

import { clearItems } from 'app/bulk-actions/bulk-actions-actions/action-creators';

const initialState: ITikTokLike2BuyState = {
  store: undefined,
  currentItemFilter: DEFAULT_TIKTOK_LIKE2BUY_FILTER,
  items: Normalized.empty(),
  pinnedItems: [],
  totalCount: 0,
  previewUrl: undefined,
  flags: {
    hasFetchedAllItems: false,
    hasFetchedInitialItems: false,
    hasFetchedPinnedItems: false,
    isAddingItems: false,
    isArchivingItems: false,
    isArchivingPinnedItems: false,
    isFetchingInitialItems: false,
    isFetchingStore: false,
    isFetchingPreviewUrl: false,
    isFetchingNextItems: false,
    isFetchingPinnedItems: false,
    isMaterializingItems: false,
    isPinningItems: false,
    isReorderingPinnedItems: false,
    isUnarchivingItems: false,
    isUnpinningItems: false,
  },
  errors: {
    addItemsError: undefined,
    archiveItemsError: undefined,
    fetchInitialItemsError: undefined,
    fetchStoreError: undefined,
    fetchPreviewUrlError: undefined,
    fetchNextItemsError: undefined,
    fetchPinnedItemsError: undefined,
    materializeError: undefined,
    pinItemsError: undefined,
    reorderPinnedItemsError: undefined,
    unarchiveItemsError: undefined,
    unpinItemsError: undefined,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.TikTokLike2Buy,
  initialState,
  reducers: {
    requestStore(state) {
      state.flags.isFetchingStore = true;
      state.errors.fetchStoreError = undefined;
    },
    receiveStoreSucceeded(state, action: PayloadAction<ILike2BuyV2Store>) {
      state.flags.isFetchingStore = false;
      state.store = action.payload;
    },
    receiveStoreFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingStore = false;
      state.errors.fetchStoreError = action.payload;
    },
    requestMaterializeItems(state) {
      state.flags.isMaterializingItems = true;
      state.errors.materializeError = undefined;
    },
    receiveMaterializeItemsSucceeded(state) {
      state.flags.isMaterializingItems = false;
    },
    receiveMaterializeItemsFailed(state, action: PayloadAction<string>) {
      state.flags.isMaterializingItems = false;
      state.errors.materializeError = action.payload;
    },
    requestPreviewUrl(state) {
      state.flags.isFetchingPreviewUrl = true;
      state.errors.fetchPreviewUrlError = undefined;
    },
    receivePreviewUrlSucceeded(state, action: PayloadAction<string>) {
      state.flags.isFetchingPreviewUrl = false;
      state.previewUrl = action.payload;
    },
    receivePreviewUrlFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingPreviewUrl = false;
      state.errors.fetchPreviewUrlError = action.payload;
    },
    setItemFilter(
      state,
      action: PayloadAction<Like2BuyV2ModerationItemFilter>,
    ) {
      state.currentItemFilter = action.payload;
      state.items = Normalized.empty();
      state.flags.hasFetchedAllItems = false;
      state.flags.hasFetchedInitialItems = false;
    },
    requestInitialItems(state) {
      state.flags.isFetchingInitialItems = true;
      state.flags.hasFetchedInitialItems = false;
      state.flags.hasFetchedAllItems = false;
      state.errors.fetchInitialItemsError = undefined;
    },
    receiveInitialItemsSucceeded(
      state,
      action: PayloadAction<ITikTokLike2BuyItemsSearchResult>,
    ) {
      const { mediaItems, totalCount } = action.payload;

      state.flags.isFetchingInitialItems = false;
      state.flags.hasFetchedInitialItems = true;
      state.items = mediaItems;
      state.totalCount = totalCount;

      if (mediaItems.orderedIds.length < LIKE2BUY_V2_ITEM_REQUEST_LIMIT) {
        state.flags.hasFetchedAllItems = true;
      }
    },
    receiveInitialItemsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingInitialItems = false;
      state.errors.fetchInitialItemsError = action.payload;
    },
    requestNextItems(state) {
      state.flags.isFetchingNextItems = true;
      state.errors.fetchNextItemsError = undefined;
    },
    receiveNextItemsSucceeded(
      state,
      action: PayloadAction<NormalizedLike2BuyV2Items>,
    ) {
      const { orderedIds } = action.payload;

      state.flags.isFetchingNextItems = false;
      state.items = Normalized.addItems(state.items, action.payload);

      if (orderedIds.length < LIKE2BUY_V2_ITEM_REQUEST_LIMIT) {
        state.flags.hasFetchedAllItems = true;
      }
    },
    receiveNextItemsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingNextItems = false;
      state.errors.fetchNextItemsError = action.payload;
    },
    requestPinnedItems(state) {
      state.flags.isFetchingPinnedItems = true;
      state.errors.fetchPinnedItemsError = undefined;
    },
    receivePinnedItemsSucceeded(
      state,
      action: PayloadAction<ILike2BuyV2MediaItem[]>,
    ) {
      state.flags.isFetchingPinnedItems = false;
      state.flags.hasFetchedPinnedItems = true;
      state.pinnedItems = action.payload;
    },
    receivePinnedItemsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingPinnedItems = false;
      state.errors.fetchPinnedItemsError = action.payload;
    },
    setPinnedItemsInState(
      state,
      action: PayloadAction<ILike2BuyV2MediaItem[]>,
    ) {
      state.pinnedItems = action.payload;
    },
    requestPinItems(state) {
      state.flags.isPinningItems = true;
      state.errors.pinItemsError = undefined;
    },
    receivePinItemsSucceeded(state) {
      state.flags.isPinningItems = false;
    },
    receivePinItemsFailed(state, action: PayloadAction<string>) {
      state.flags.isPinningItems = false;
      state.errors.pinItemsError = action.payload;
    },
    requestUnpinItems(state) {
      state.flags.isUnpinningItems = true;
      state.errors.unpinItemsError = undefined;
    },
    receiveUnpinItemsSucceeded(state) {
      state.flags.isUnpinningItems = false;
    },
    receiveUnpinItemsFailed(state, action: PayloadAction<string>) {
      state.flags.isUnpinningItems = false;
      state.errors.unpinItemsError = action.payload;
    },
    requestReorderPinnedItems(state) {
      state.flags.isReorderingPinnedItems = true;
      state.errors.reorderPinnedItemsError = undefined;
    },
    receiveReorderPinnedItemsSucceeded(state) {
      state.flags.isReorderingPinnedItems = false;
    },
    receiveReorderPinnedItemsFailed(state, action: PayloadAction<string>) {
      state.flags.isReorderingPinnedItems = false;
      state.errors.reorderPinnedItemsError = action.payload;
    },
    updateItemMetadata(state, action: PayloadAction<ILike2BuyV2MediaItem[]>) {
      const updatedItemsById = PojoMap.fromIndexing(
        action.payload,
        (item) => item.cuid,
      );

      state.items.byId = PojoMap.union(state.items.byId, updatedItemsById);
    },
    addItemsToState(state, action: PayloadAction<ILike2BuyV2MediaItem[]>) {
      const allGridItems = Normalized.getOrdered(state.items);
      const sortedGridItems = [...allGridItems, ...action.payload].sort(
        (itemA, itemB) => {
          if (!itemA.networkItem || !itemB.networkItem) {
            return 0;
          }
          return itemA.networkItem.postedTime <= itemB.networkItem.postedTime
            ? 1
            : -1;
        },
      );

      state.items = normalize(sortedGridItems, (item) => item.cuid);
      state.totalCount += action.payload.length;
    },
    removeItemsFromState(state, action: PayloadAction<ILike2BuyV2MediaItem[]>) {
      const itemCuids = action.payload.map((item) => item.cuid);
      const itemsById = state.items.byId;
      itemCuids.forEach((cuid) => PojoMap.remove(itemsById, cuid));

      state.items = Normalized.removeItems(state.items, itemCuids);
      state.totalCount -= action.payload.length;
    },
    requestAddItems(state) {
      state.flags.isAddingItems = true;
      state.errors.addItemsError = undefined;
    },
    receiveAddItemsSucceeded(state) {
      state.flags.isAddingItems = false;
    },
    receiveAddItemsFailed(state, action: PayloadAction<string>) {
      state.flags.isAddingItems = false;
      state.errors.addItemsError = action.payload;
    },
    requestUnarchiveItems(state) {
      state.flags.isUnarchivingItems = true;
      state.errors.unarchiveItemsError = undefined;
    },
    receiveUnarchiveItemsSucceeded(state) {
      state.flags.isUnarchivingItems = false;
    },
    receiveUnarchiveItemsFailed(state, action: PayloadAction<string>) {
      state.flags.isUnarchivingItems = false;
      state.errors.unarchiveItemsError = action.payload;
    },
    requestArchiveItems(state, action: PayloadAction<ILike2BuyV2MediaItem[]>) {
      state.flags.isArchivingItems = true;
      state.errors.archiveItemsError = undefined;

      const pinnedItemCuids = state.pinnedItems.map((item) => item.cuid);
      if (
        action.payload.filter((item) => pinnedItemCuids.includes(item.cuid))
          .length > 0
      ) {
        state.flags.isArchivingPinnedItems = true;
      }
    },
    receiveArchiveItemsSucceeded(state) {
      state.flags.isArchivingItems = false;
      state.flags.isArchivingPinnedItems = false;
    },
    receiveArchiveItemsFailed(state, action: PayloadAction<string>) {
      state.flags.isArchivingItems = false;
      state.errors.archiveItemsError = action.payload;
      state.flags.isArchivingPinnedItems = false;
    },
  },
});

export const { receiveStoreSucceeded } = actions;

export const getCurrentStore = createThunk(() => async (dispatch, getState) => {
  const state = getState();
  const tiktokUserId = getTikTokUserId(state);

  try {
    dispatch(actions.requestStore());

    if (isUndefined(tiktokUserId)) {
      throw new Error('No TikTok account found for client');
    }

    const stores = await findLike2BuyV2Stores();

    if (isUndefined(stores)) {
      throw new Error('No Like2Buy stores found for client');
    }

    const store = stores.find(
      (store) => `${store.networkUserId}` === `${tiktokUserId}`,
    );

    if (isUndefined(store)) {
      throw new Error(
        `No TikTok Like2Buy store found for TikTok user ${tiktokUserId}`,
      );
    }

    dispatch(actions.receiveStoreSucceeded(store));
  } catch (e) {
    dispatch(
      actions.receiveStoreFailed(
        getRequestErrorMessage(e) ??
          '[getCurrentStore] unknown error getting current like2buy store',
      ),
    );
  }
});

export const getPreviewUrl = createThunk(() => async (dispatch, getState) => {
  const state = getState();
  const storeId = getTikTokLike2BuyStoreId(state);

  try {
    dispatch(actions.requestPreviewUrl());

    if (isUndefined(storeId)) {
      throw new Error('StoreId is required to get preview');
    }

    const previewUrl = await getLike2BuyV2PreviewUrl(storeId);

    dispatch(actions.receivePreviewUrlSucceeded(previewUrl));
  } catch (e) {
    dispatch(
      actions.receivePreviewUrlFailed(
        getRequestErrorMessage(e) ??
          '[getTikTokLike2BuyPreviewUrl] unknown failure to get like2buy preview url',
      ),
    );
  }
});

export const addContentDrawerItems = createThunk(
  (itemsToAdd: IContentLibraryItem[]) =>
    async (dispatch, getState): Promise<ILike2BuyV2MediaItem[] | undefined> => {
      const state = getState();
      const clientId = getClientId(state);
      const storeId = getTikTokLike2BuyStoreId(state);
      const currentItemFilter = getTikTokLike2BuyCurrentItemFilter(state);
      const statusForRequest = getLike2BuyV2StatusForRequest(currentItemFilter);

      const cuidsToAdd = itemsToAdd.map((item) => Cuid.build(item, clientId));

      try {
        dispatch(actions.requestAddItems());

        if (isUndefined(storeId)) {
          throw new Error('StoreId is required to add items');
        }

        const { alreadyAddedItems } = await addLike2BuyV2Items(storeId, {
          // without setting this to true the subsequent getLike2BuyV2Items response doesn't always have the added items yet
          consistentWrite: true,
          cuids: cuidsToAdd,
        });

        dispatch(actions.receiveAddItemsSucceeded());
        const addedItemCount = cuidsToAdd.length - alreadyAddedItems.length;

        if (addedItemCount > 0) {
          dispatch(
            toastNotificationsActions.addToastNotification(
              addLike2BuyV2ItemsSucceededToast(
                addedItemCount,
                SocialNetwork.TikTok,
              ),
            ),
          );

          // after successfully adding content drawer items to like2buy store we need to update the ui by calling getInitialItems
          await dispatch(getInitialItems(statusForRequest));
        }

        return toLike2BuyV2MediaItems(alreadyAddedItems);
      } catch (e) {
        dispatch(
          actions.receiveAddItemsFailed(
            getRequestErrorMessage(e) ??
              '[addTikTokLike2BuyItems] unknown failure to add like2buy content drawer items',
          ),
        );
        dispatch(
          toastNotificationsActions.addToastNotification(
            addLike2BuyV2ItemsFailedToast(SocialNetwork.TikTok),
          ),
        );
      }
    },
);

const fetchItems = async (
  storeId: Like2BuyV2StoreId,
  offset: number = 0,
  statusForRequest?: Like2BuyV2ModerationItemFilter,
) => {
  const { mediaItems, totalCount } = await getLike2BuyV2Items(storeId, {
    status: statusForRequest,
    offset,
    limit: LIKE2BUY_V2_ITEM_REQUEST_LIMIT,
  });

  return {
    mediaItems: toLike2BuyV2NormalizedMediaItems(mediaItems),
    totalCount,
  };
};

/**
 * Gets first set of Like2Buy items. Should be used on page load or after selecting a new filter.
 */
export const getInitialItems = createThunk(
  (
      selectedFilter: Like2BuyV2ModerationItemFilter = DEFAULT_TIKTOK_LIKE2BUY_FILTER,
    ) =>
    async (dispatch, getState) => {
      const state = getState();
      const storeId = getTikTokLike2BuyStoreId(state);
      const selectedItems = getTikTokLike2BuySelectedItems(state);

      // if bulk items are selected and no action has taken place the items will remain selected
      if (selectedItems.length > 0) {
        dispatch(clearItems());
      }

      dispatch(actions.setItemFilter(selectedFilter));

      const statusForRequest = getLike2BuyV2StatusForRequest(selectedFilter);

      try {
        dispatch(actions.requestInitialItems());

        if (isUndefined(storeId)) {
          throw new Error('StoreId is required to fetch items');
        }

        const { mediaItems, totalCount } = await fetchItems(
          storeId,
          0,
          statusForRequest,
        );

        dispatch(
          actions.receiveInitialItemsSucceeded({
            mediaItems,
            totalCount,
          }),
        );
      } catch (e) {
        dispatch(
          actions.receiveInitialItemsFailed(
            getRequestErrorMessage(e) ??
              '[getTikTokLike2BuyInitialItems] unknown failure to get initial like2buy items',
          ),
        );
      }
    },
);

/**
 * Gets additional Like2Buy items. Should be used when user scrolls to the bottom of the item grid.
 */
export const getNextItems = createThunk(() => async (dispatch, getState) => {
  const state = getState();

  const storeId = getTikTokLike2BuyStoreId(state);
  const currentItems = getTikTokLike2BuyOrderedItems(state);
  const currentItemFilter = getTikTokLike2BuyCurrentItemFilter(state);
  const { hasFetchedAllItems, hasFetchedInitialItems, isFetchingNextItems } =
    getTikTokLike2BuyFlags(state);

  const statusForRequest = getLike2BuyV2StatusForRequest(currentItemFilter);

  if (!hasFetchedInitialItems || isFetchingNextItems || hasFetchedAllItems) {
    return;
  }

  try {
    dispatch(actions.requestNextItems());

    if (isUndefined(storeId)) {
      throw new Error('StoreId is required to fetch items');
    }

    const { mediaItems } = await fetchItems(
      storeId,
      currentItems.length,
      statusForRequest,
    );

    dispatch(actions.receiveNextItemsSucceeded(mediaItems));
  } catch (e) {
    dispatch(
      actions.receiveNextItemsFailed(
        getRequestErrorMessage(e) ??
          '[getTikTokLike2BuyNextItems] unknown failure to get next like2buy items',
      ),
    );
  }
});

// This is used to materialize items after they've been changed so it is reflected in the UI before all items are fetched again
const materializeItemsByCuid = createThunk(
  (items: ILike2BuyV2MediaItem[], storeId: Like2BuyV2StoreId) =>
    async (dispatch) => {
      const cuids = items.map((item) => item.cuid);

      try {
        dispatch(actions.requestMaterializeItems());

        const itemsWithStatus = await materializeLike2BuyV2ItemsByCuid(
          storeId,
          {
            cuids,
          },
        );

        dispatch(actions.receiveMaterializeItemsSucceeded());

        return toLike2BuyV2MediaItems(itemsWithStatus);
      } catch (e) {
        dispatch(
          actions.receiveMaterializeItemsFailed(
            getRequestErrorMessage(e) ??
              '[materializeItemsByCuid] unknown error materializing items',
          ),
        );
      }
    },
);

export const setPinnedItems = createThunk(
  (newPinnedItems: ILike2BuyV2MediaItem[], storeId: Like2BuyV2StoreId) =>
    async (dispatch, getState) => {
      const state = getState();
      const pinnedItems = getTikTokLike2BuyPinnedItemsFromStore(state);

      const newPinnedItemCuids = newPinnedItems.map((item) => item.cuid);

      try {
        // update the pinned items preemptively to prevent UI lag
        dispatch(actions.setPinnedItemsInState(newPinnedItems));

        await setPinnedLike2BuyV2Items(storeId, {
          cuids: newPinnedItemCuids,
          consistentWrite: true,
        });

        void dispatch(getPinnedItems());
      } catch (e) {
        // revert to existing pinned items if there's an error
        dispatch(actions.setPinnedItemsInState(pinnedItems));
        // throw error to be handled by consumers - pinItems, unpinItems, and reorderPinnedItems
        throw new Error(
          getRequestErrorMessage(e) ??
            '[setTikTokLike2BuyPinnedItems] unknown failure to set pinned like2buy items',
        );
      }
    },
);

export const pinItems = createThunk(
  (itemsToPin: ILike2BuyV2MediaItem[]) => async (dispatch, getState) => {
    const state = getState();

    const storeId = getTikTokLike2BuyStoreId(state);
    const pinnedItems = getTikTokLike2BuyPinnedItemsFromStore(state);
    const gridItems = getTikTokLike2BuyFilteredOrderedItems(state);

    const newItems = itemsToPin.map((item) => ({
      ...item,
      like2BuyV2ModerationStatus: Like2BuyV2ModerationStatus.Pinned,
    }));
    const newPinnedItems = [...pinnedItems, ...newItems];

    if (newPinnedItems.length === MAXIMUM_PINNED_ITEMS_COUNT) {
      dispatch(
        toastNotificationsActions.addToastNotification(
          like2BuyV2MaxPinnedItemsWarningToast(SocialNetwork.TikTok),
        ),
      );
    }

    // calls to pin more than the allowed max will not be attempted and will display a failure toast
    // we should not encounter this case
    if (newPinnedItems.length > MAXIMUM_PINNED_ITEMS_COUNT) {
      dispatch(
        toastNotificationsActions.addToastNotification(
          maxPinsLike2BuyV2ItemsFailedToast(SocialNetwork.TikTok),
        ),
      );
      return;
    }

    try {
      if (isUndefined(storeId)) {
        throw new Error('storeId is required to pin items');
      }

      dispatch(actions.requestPinItems());
      // optimistically update grid ui
      dispatch(actions.updateItemMetadata(newItems));

      await dispatch(setPinnedItems(newPinnedItems, storeId));

      const updatedItems = await dispatch(
        materializeItemsByCuid(itemsToPin, storeId),
      );
      // we want to update the ui with items' changed metadata
      if (isDefined(updatedItems)) {
        dispatch(actions.updateItemMetadata(updatedItems));
      }

      dispatch(actions.receivePinItemsSucceeded());
      dispatch(
        toastNotificationsActions.addToastNotification(
          pinLike2BuyV2ItemsSucceededToast(
            itemsToPin.length,
            SocialNetwork.TikTok,
          ),
        ),
      );
    } catch (e) {
      // if this fails we want to update ui with original pinned and grid items
      if (isDefined(storeId)) {
        void dispatch(setPinnedItems(pinnedItems, storeId));
      }
      dispatch(actions.updateItemMetadata(gridItems));
      dispatch(
        actions.receivePinItemsFailed(
          getRequestErrorMessage(e) ??
            '[pinItems] unknown failure to pin like2buy items',
        ),
      );
      dispatch(
        toastNotificationsActions.addToastNotification(
          pinLike2BuyV2ItemsFailedToast(
            itemsToPin.length,
            SocialNetwork.TikTok,
          ),
        ),
      );
    }
  },
);

export const unpinItems = createThunk(
  (itemsToUnpin: ILike2BuyV2MediaItem[]) => async (dispatch, getState) => {
    const state = getState();

    const storeId = getTikTokLike2BuyStoreId(state);

    const pinnedItems = getTikTokLike2BuyPinnedItemsFromStore(state);
    const newPinnedItems = pinnedItems.filter(
      (pinnedItem) =>
        !itemsToUnpin.map((item) => item.cuid).includes(pinnedItem.cuid),
    );

    const gridItems = getTikTokLike2BuyFilteredOrderedItems(state);
    const newGridItems = itemsToUnpin.map((item) => ({
      ...item,
      like2BuyV2ModerationStatus: Like2BuyV2ModerationStatus.Published,
    }));

    try {
      if (isUndefined(storeId)) {
        throw new Error('storeId is required to pin items');
      }

      dispatch(actions.requestUnpinItems());
      // optimistically update grid ui
      dispatch(actions.updateItemMetadata(newGridItems));

      await dispatch(setPinnedItems(newPinnedItems, storeId));

      const updatedItems = await dispatch(
        materializeItemsByCuid(itemsToUnpin, storeId),
      );
      // we want to update the ui with items' changed metadata
      isDefined(updatedItems) &&
        dispatch(actions.updateItemMetadata(updatedItems));

      dispatch(actions.receiveUnpinItemsSucceeded());
      dispatch(
        toastNotificationsActions.addToastNotification(
          unpinLike2BuyV2ItemsSucceededToast(
            itemsToUnpin.length,
            SocialNetwork.TikTok,
          ),
        ),
      );
    } catch (e) {
      // if this fails we want to update ui with original pinned and grid items
      if (isDefined(storeId)) {
        void dispatch(setPinnedItems(pinnedItems, storeId));
      }
      dispatch(actions.updateItemMetadata(gridItems));
      dispatch(
        actions.receiveUnpinItemsFailed(
          getRequestErrorMessage(e) ??
            '[unpinItems] unknown failure to unpin like2buy items',
        ),
      );
      dispatch(
        toastNotificationsActions.addToastNotification(
          unpinLike2BuyV2ItemsFailedToast(
            itemsToUnpin.length,
            SocialNetwork.TikTok,
          ),
        ),
      );
    }
  },
);

export const reorderPinnedItems = createThunk(
  (newPinnedItems: ILike2BuyV2MediaItem[]) => async (dispatch, getState) => {
    const state = getState();
    const storeId = getTikTokLike2BuyStoreId(state);

    try {
      if (isUndefined(storeId)) {
        throw new Error('StoreId is required to set pinned items');
      }

      dispatch(actions.requestReorderPinnedItems());

      await dispatch(setPinnedItems(newPinnedItems, storeId));

      dispatch(actions.receiveReorderPinnedItemsSucceeded());
    } catch (e) {
      dispatch(
        actions.receiveReorderPinnedItemsFailed(
          getRequestErrorMessage(e) ??
            '[reorderTikTokLike2BuyPinnedItems] unknown failure to reorder pinned like2buy items',
        ),
      );
      dispatch(
        toastNotificationsActions.addToastNotification(
          reorderLike2BuyV2PinnedItemsFailedToast(SocialNetwork.TikTok),
        ),
      );
    }
  },
);

export const archiveItems = createThunk(
  (itemsToArchive: ILike2BuyV2MediaItem[]) => async (dispatch, getState) => {
    const state = getState();
    const storeId = getTikTokLike2BuyStoreId(state);
    const currentItemFilter = getTikTokLike2BuyCurrentItemFilter(state);

    const itemCuidsToArchive = itemsToArchive.map((item) => item.cuid);

    const pinnedItems = getTikTokLike2BuyPinnedItemsFromStore(state);
    const updatedPinnedItems = pinnedItems.filter(
      (pinnedItem) => !itemCuidsToArchive.includes(pinnedItem.cuid),
    );

    const gridItems = getTikTokLike2BuyFilteredOrderedItems(state);
    const newGridItems = itemsToArchive.map((item) => ({
      ...item,
      like2BuyV2ModerationStatus: Like2BuyV2ModerationStatus.Archived,
    }));

    try {
      if (isDefined(storeId)) {
        dispatch(actions.requestArchiveItems(itemsToArchive));
        // optimistically update grid ui
        dispatch(actions.updateItemMetadata(newGridItems));
        // if filter is Archived we need to add the item to the grid
        if (currentItemFilter === Like2BuyV2ModerationItemFilter.Archived) {
          dispatch(actions.addItemsToState(newGridItems));
        }
        // optimistically update pinned media ui if archiving a pinned item
        if (updatedPinnedItems.length !== pinnedItems.length) {
          dispatch(actions.setPinnedItemsInState(updatedPinnedItems));
        }

        await archiveLike2BuyV2Items(storeId, {
          cuids: itemCuidsToArchive,
          consistentWrite: true,
        });

        if (updatedPinnedItems.length !== pinnedItems.length) {
          void dispatch(getPinnedItems());
        }
        const updatedItems = await dispatch(
          materializeItemsByCuid(itemsToArchive, storeId),
        );
        // we want to update the ui with items' changed metadata
        if (isDefined(updatedItems)) {
          dispatch(actions.updateItemMetadata(updatedItems));
        }

        dispatch(actions.receiveArchiveItemsSucceeded());
        dispatch(
          toastNotificationsActions.addToastNotification(
            archiveLike2BuyV2ItemsSucceededToast(
              itemsToArchive.length,
              SocialNetwork.TikTok,
            ),
          ),
        );
      }
    } catch (e) {
      // if this fails we want to update ui with original pinned and grid items
      dispatch(actions.updateItemMetadata(gridItems));
      if (currentItemFilter === Like2BuyV2ModerationItemFilter.Archived) {
        dispatch(actions.removeItemsFromState(newGridItems));
      }
      if (updatedPinnedItems.length !== pinnedItems.length) {
        dispatch(actions.setPinnedItemsInState(pinnedItems));
      }
      dispatch(
        actions.receiveArchiveItemsFailed(
          getRequestErrorMessage(e) ??
            '[archiveTikTokLike2BuyItems] unknown failure to archive like2buy items',
        ),
      );
      dispatch(
        toastNotificationsActions.addToastNotification(
          archiveLike2BuyV2ItemsFailedToast(
            itemsToArchive.length,
            SocialNetwork.TikTok,
          ),
        ),
      );
    }
  },
);

export const unarchiveItems = createThunk(
  (itemsToUnarchive: ILike2BuyV2MediaItem[]) => async (dispatch, getState) => {
    const state = getState();
    const storeId = getTikTokLike2BuyStoreId(state);

    const itemCuidsToUnarchive = itemsToUnarchive.map((item) => item.cuid);

    const gridItems = getTikTokLike2BuyFilteredOrderedItems(state);
    const newGridItems: ILike2BuyV2MediaItem[] = itemsToUnarchive.map(
      (item) => {
        if (getDoesItemNeedRightsAndProducts(item)) {
          return {
            ...item,
            like2BuyV2ModerationStatus:
              Like2BuyV2ModerationStatus.NeedsProductsAndRights,
          };
        }

        if (getDoesItemNeedProducts(item)) {
          return {
            ...item,
            like2BuyV2ModerationStatus:
              Like2BuyV2ModerationStatus.NeedsProducts,
          };
        }

        if (getDoesItemNeedRights(item)) {
          return {
            ...item,
            like2BuyV2ModerationStatus: Like2BuyV2ModerationStatus.NeedsRights,
          };
        }

        return {
          ...item,
          like2BuyV2ModerationStatus: Like2BuyV2ModerationStatus.Published,
        };
      },
    );

    try {
      dispatch(actions.requestUnarchiveItems());

      if (isUndefined(storeId)) {
        throw new Error('storeId required to unarchive items');
      }
      // optimistically update grid ui
      dispatch(actions.updateItemMetadata(newGridItems));

      await addLike2BuyV2Items(storeId, {
        cuids: itemCuidsToUnarchive,
      });

      const updatedItems = await dispatch(
        materializeItemsByCuid(itemsToUnarchive, storeId),
      );
      // we want to update the ui with items' changed metadata
      if (isDefined(updatedItems)) {
        dispatch(actions.updateItemMetadata(updatedItems));
      }

      dispatch(actions.receiveUnarchiveItemsSucceeded());
      dispatch(
        toastNotificationsActions.addToastNotification(
          unarchiveLike2BuyV2ItemsSucceededToast(
            itemsToUnarchive.length,
            SocialNetwork.TikTok,
          ),
        ),
      );

      const numberOfAddedItems =
        updatedItems?.filter(
          (item) =>
            item.like2BuyV2ModerationStatus ===
            Like2BuyV2ModerationStatus.Published,
        ).length ?? 0;

      if (numberOfAddedItems > 0) {
        dispatch(
          toastNotificationsActions.addToastNotification(
            addLike2BuyV2ItemsSucceededToast(
              numberOfAddedItems,
              SocialNetwork.TikTok,
            ),
          ),
        );
      }
    } catch (e) {
      // if this fails we want to update ui with original grid items
      dispatch(actions.updateItemMetadata(gridItems));
      dispatch(
        actions.receiveUnarchiveItemsFailed(
          getRequestErrorMessage(e) ??
            '[unarchiveTikTokLike2BuyItems] unknown failure to unarchive like2buy items',
        ),
      );
      dispatch(
        toastNotificationsActions.addToastNotification(
          unarchiveLike2BuyV2ItemsFailedToast(
            itemsToUnarchive.length,
            SocialNetwork.TikTok,
          ),
        ),
      );
    }
  },
);

/**
 * Handles updating Like2Buy item after UIA is closed.
 * Refetches item regardless of response in order to have the correct tooltips if a rights request was made.
 *
 */
export const handleTikTokLike2BuyUiaCloseResponse = createThunk(
  (response: IUiaModalClosedResponse) => async (dispatch, getState) => {
    const state = getState();

    const clientId = getClientId(state);
    const storeId = getTikTokLike2BuyStoreId(state);
    const { items } = getTikTokLike2Buy(state);

    if (isUndefined(storeId)) {
      return;
    }

    const cuid = Cuid.fromPartial(
      PartialCuid(response.universalItemId),
      clientId,
    );

    const like2buyItem = Normalized.getById(items, cuid);

    if (isDefined(like2buyItem)) {
      const updatedItems = await dispatch(
        materializeItemsByCuid([like2buyItem], storeId),
      );
      const updatedItem = updatedItems?.[0];
      // we want to update the ui with items' changed metadata if the products changed
      if (isDefined(updatedItem)) {
        dispatch(actions.updateItemMetadata([updatedItem]));

        // remove from pinned items if last product removed from pinned item
        if (
          like2buyItem.like2BuyV2ModerationStatus ===
            Like2BuyV2ModerationStatus.Pinned &&
          updatedItem.like2BuyV2ModerationStatus ===
            Like2BuyV2ModerationStatus.NeedsProducts
        ) {
          void dispatch(unpinItems([like2buyItem]));
        }

        // If the item changes from needs products to published or published to needs products
        // we want to invalidate the item cache so the consumer experience reflects the change immediately.
        if (
          (like2buyItem.like2BuyV2ModerationStatus ===
            Like2BuyV2ModerationStatus.NeedsProducts &&
            updatedItem.like2BuyV2ModerationStatus ===
              Like2BuyV2ModerationStatus.Published) ||
          (like2buyItem.like2BuyV2ModerationStatus ===
            Like2BuyV2ModerationStatus.Pinned &&
            updatedItem.like2BuyV2ModerationStatus ===
              Like2BuyV2ModerationStatus.NeedsProducts) ||
          (like2buyItem.like2BuyV2ModerationStatus ===
            Like2BuyV2ModerationStatus.Published &&
            updatedItem.like2BuyV2ModerationStatus ===
              Like2BuyV2ModerationStatus.NeedsProducts)
        ) {
          await clearLike2BuyV2ItemCache(storeId);
        }
      }
    }
  },
);

export const getPinnedItems = createThunk(() => async (dispatch, getState) => {
  dispatch(actions.requestPinnedItems());
  const storeId = getTikTokLike2BuyStoreId(getState());

  try {
    if (isUndefined(storeId)) {
      throw new Error('StoreId is required to get pinned items');
    }
    const pinnedItems = await getPinnedLike2BuyV2Items(storeId);

    dispatch(
      actions.receivePinnedItemsSucceeded(toLike2BuyV2MediaItems(pinnedItems)),
    );
  } catch (e) {
    dispatch(
      actions.receivePinnedItemsFailed(
        getRequestErrorMessage(e) ??
          '[getPinnedItems] unknown failure to get pinned like2buy items',
      ),
    );
  }
});

export { reducer };
